import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import PropTypes from "prop-types";
import moment from "moment";
import { useSelector, useDispatch } from "react-redux";

import Layout from "layouts/DashboardManage";
import Pagination from "components/Pagination";
import ManageDataTable from "components/ManageDataTable";
import Notification from "components/Notification";
import {
  getAllFiles,
  generateCSVLink,
  GENERATE_CSV,
  resetPayload
} from "store/actions/fileAction";
import { SpinnerRainbow } from "components/Spinner";
import NoData from "components/NoData";
import Translate from "helpers/Translate";
import Filters from "./Filters/Filters";
import * as fileStatuses from "helpers/fileStatus.json";
import FileDetails from "./FileDetails";

// Styles
import "./FileHistory.styles.scss";

const FileHistory = () => {
  const dispatch = useDispatch();
  const isLoadingFile = useSelector(state => state.file.isLoadingFile);
  const allFiles = useSelector(state => state.file.getFiles);
  const generateCSV = useSelector(state => state.file.generateCSV);

  // Set filter data in local states
  const [selectedFileId, setSelectedFileId] = useState("");
  const [selectedOriginalFilename, setSelectedOriginalFilename] = useState("");
  const [selectedMetadataNames, setSelectedMetadataNames] = useState([]);
  const [selectedDesktopUsernames, setSelectedDesktopUsernames] = useState([]);
  const [selectedWebUsernames, setSelectedWebUsernames] = useState([]);
  const [selectedClients, setSelectedClients] = useState([]);
  const [selectedDepartments, setSelectedDepartment] = useState([]);
  const [selectedDepartmentItemValues, setSelectedDepartmentItemValues] =
    useState([]);
  const [selectedDataEntryStartDate, setSelectedDataEntryStartDate] =
    useState("");
  const [selectedDataEntryEndDate, setSelectedDataEntryEndDate] = useState("");
  const [
    selectedWebUserApprovalStartDate,
    setSelectedWebUserApprovalStartDate
  ] = useState("");
  const [selectedWebUserApprovalEndDate, setSelectedWebUserApprovalEndDate] =
    useState("");
  const [selectedDmsUploadStartDate, setSelectedDmsUploadStartDate] =
    useState("");
  const [selectedDmsUploadEndDate, setSelectedDmsUploadEndDate] = useState("");
  const [selectedStatus, setSelectedStatus] = useState(
    fileStatuses.STATUS_SENT
  );
  const [selectedPageLimit, setSelectedPageLimit] = useState(10);

  // Get file list
  useEffect(() => {
    dispatch(getAllFiles({ status: selectedStatus }));
    // eslint-disable-next-line
  }, [dispatch]);

  // Show notification on csv acceptance
  useEffect(() => {
    if (generateCSV) {
      Notification.success(generateCSV);
      dispatch(resetPayload(GENERATE_CSV));
    }
  }, [generateCSV, dispatch]);

  const handleFilterData = ({ page = 1, resetFilter, filestatus }) => {
    dispatch(
      getAllFiles({
        fileId: !resetFilter ? selectedFileId : "",
        rawFilename: !resetFilter ? selectedOriginalFilename : "",
        metadataNames: !resetFilter
          ? selectedMetadataNames?.map(item => item.id)
          : [],
        clients: !resetFilter ? selectedClients?.map(item => item.id) : [],
        desktopUsernames: !resetFilter
          ? selectedDesktopUsernames.map(item => item.id)
          : [],
        webUsernames: !resetFilter
          ? selectedWebUsernames?.map(item => item.id)
          : [],
        departments: !resetFilter
          ? selectedDepartments?.map(item => item.id)
          : [],
        departmentItemValues: !resetFilter
          ? selectedDepartmentItemValues?.map(item => item.value)
          : [],
        // Data Entry Range
        dataEntryStartDate: !resetFilter ? selectedDataEntryStartDate : "",
        dataEntryEndDate: !resetFilter ? selectedDataEntryEndDate : "",
        // Web User Approval Range
        webUserApprovalStartDate: !resetFilter
          ? selectedWebUserApprovalStartDate
          : "",
        webUserApprovalEndDate: !resetFilter
          ? selectedWebUserApprovalEndDate
          : "",
        // DMS Upload Range
        dmsUploadStartDate: !resetFilter ? selectedDmsUploadStartDate : "",
        dmsUploadEndDate: !resetFilter ? selectedDmsUploadEndDate : "",
        status: resetFilter ? filestatus : selectedStatus,
        page,
        limit: !resetFilter ? selectedPageLimit : ""
      })
    );
  };

  const handleResetFilter = () => {
    Notification.confirm(Translate({ id: "sure_to_reset" }), {
      onOk: () => {
        setSelectedFileId("");
        setSelectedOriginalFilename("");
        setSelectedMetadataNames([]);
        setSelectedDesktopUsernames([]);
        setSelectedWebUsernames([]);
        setSelectedClients([]);
        setSelectedDepartment([]);
        setSelectedDepartmentItemValues([]);
        // Data Entry Range
        setSelectedDataEntryStartDate("");
        setSelectedDataEntryEndDate("");
        // Web User Approval Range
        setSelectedWebUserApprovalStartDate("");
        setSelectedWebUserApprovalEndDate("");
        // DMS Upload Range
        setSelectedDmsUploadStartDate("");
        setSelectedDmsUploadEndDate("");
        setSelectedStatus(fileStatuses.STATUS_SENT);
        setSelectedPageLimit("");

        handleFilterData({
          resetFilter: true,
          filestatus: fileStatuses.STATUS_SENT
        });
      },
      labelOk: Translate({ id: "yes" }),
      labelCancel: Translate({ id: "no" })
    });
  };

  const handleExportCSV = () => {
    dispatch(
      generateCSVLink({
        fileId: selectedFileId,
        rawFilename: selectedOriginalFilename,
        metadataNames: selectedMetadataNames?.map(item => item.id),
        clients: selectedClients?.map(item => item.id),
        desktopUsernames: selectedDesktopUsernames.map(item => item.id),
        webUsernames: selectedWebUsernames?.map(item => item.id),
        departments: selectedDepartments?.map(item => item.id),
        departmentItemValues: selectedDepartmentItemValues?.map(
          item => item.value
        ),
        // Data Entry Range
        dataEntryStartDate: selectedDataEntryStartDate,
        dataEntryEndDate: selectedDataEntryEndDate,
        // Web User Approval Range
        webUserApprovalStartDate: selectedWebUserApprovalStartDate,
        webUserApprovalEndDate: selectedWebUserApprovalEndDate,
        // DMS Upload Range
        dmsUploadStartDate: selectedDmsUploadStartDate,
        dmsUploadEndDate: selectedDmsUploadEndDate,
        status: selectedStatus,
        jobType: "GENERATE_CSV"
      })
    );
  };
  const handleExportBillingCSV = () => {
    dispatch(
      generateCSVLink({
        fileId: selectedFileId,
        rawFilename: selectedOriginalFilename,
        metadataNames: selectedMetadataNames?.map(item => item.id),
        clients: selectedClients?.map(item => item.id),
        desktopUsernames: selectedDesktopUsernames.map(item => item.id),
        webUsernames: selectedWebUsernames?.map(item => item.id),
        departments: selectedDepartments?.map(item => item.id),
        departmentItemValues: selectedDepartmentItemValues?.map(
          item => item.value
        ),
        // Data Entry Range
        dataEntryStartDate: selectedDataEntryStartDate,
        dataEntryEndDate: selectedDataEntryEndDate,
        // Web User Approval Range
        webUserApprovalStartDate: selectedWebUserApprovalStartDate,
        webUserApprovalEndDate: selectedWebUserApprovalEndDate,
        // DMS Upload Range
        dmsUploadStartDate: selectedDmsUploadStartDate,
        dmsUploadEndDate: selectedDmsUploadEndDate,
        status: selectedStatus,
        jobType: "GENERATE_BILLING_CSV"
      })
    );
  };
  const onPageChange = data => {
    const targetPage = parseInt(data.selected) + 1;
    handleFilterData({ page: targetPage });
  };

  const hasData = () => {
    return !isLoadingFile && allFiles?.data && allFiles.data.length > 0;
  };

  const getUploadDate = item => {
    let uploadDate = "--";
    let data = item.history.find(
      history => history.status === fileStatuses.STATUS_UPLOADED
    );
    if (data) {
      uploadDate = moment(data?.created_at).format("YYYY-MM-DD H:m");
    }

    return uploadDate;
  };
  const getDataEntryDate = item => {
    let dataEntryDate = "--";
    let data = item.history.find(
      history => history.status === fileStatuses.STATUS_SENT
    );
    if (data) {
      dataEntryDate = moment(data?.created_at).format("YYYY-MM-DD H:m");
    }
    return dataEntryDate;
  };
  return (
    <Layout>
      <div className="manage">
        <Link className="back mb-20" to="/manage">
          <i className="fas fa-long-arrow-alt-left"></i>
          {Translate({ id: "back_to_manage_dashboard" })}
        </Link>

        <div className="file-history__page-header">
          <div className="manage-page-heading mb-0">
            <span className="manage-page-icon tone-amber" aria-hidden="true">
              <i className="fas fa-history"></i>
            </span>
            <div className="manage-page-heading__text">
              <h1 className="title">{Translate({ id: "file_history" })}</h1>
              <p className="manage-page-subtitle">
                {Translate({ id: "file_history_description" })}
              </p>
            </div>
          </div>
          {!isLoadingFile && (
            <div className="file-history__page-actions">
              <button
                type="button"
                className="btn file-history__btn-export"
                onClick={handleExportCSV}
              >
                <i className="fas fa-file-csv mr-10"></i>
                {Translate({ id: "export_to_csv" })}
              </button>
              <button
                type="button"
                className="btn btn-primary file-history__btn-billing"
                onClick={handleExportBillingCSV}
              >
                <i className="fas fa-file-invoice-dollar mr-10"></i>
                {Translate({ id: "billing_report_csv" })}
              </button>
            </div>
          )}
        </div>

        <div className="file-history__filters mb-20">
          <Filters
            handleFilterData={handleFilterData}
            handleResetFilter={handleResetFilter}
            selectedFileId={selectedFileId}
            setSelectedFileId={setSelectedFileId}
            selectedOriginalFilename={selectedOriginalFilename}
            setSelectedOriginalFilename={setSelectedOriginalFilename}
            selectedMetadataNames={selectedMetadataNames}
            setSelectedMetadataNames={setSelectedMetadataNames}
            selectedDesktopUsernames={selectedDesktopUsernames}
            setSelectedDesktopUsernames={setSelectedDesktopUsernames}
            selectedWebUsernames={selectedWebUsernames}
            setSelectedWebUsernames={setSelectedWebUsernames}
            selectedClients={selectedClients}
            setSelectedClients={setSelectedClients}
            selectedDepartments={selectedDepartments}
            setSelectedDepartments={setSelectedDepartment}
            selectedCollectionItemValues={selectedDepartmentItemValues}
            setSelectedCollectionItemValues={setSelectedDepartmentItemValues}
            selectedDataEntryStartDate={selectedDataEntryStartDate}
            selectedDataEntryEndDate={selectedDataEntryEndDate}
            setSelectedDataEntryStartDate={setSelectedDataEntryStartDate}
            setSelectedDataEntryEndDate={setSelectedDataEntryEndDate}
            selectedWebUserApprovalStartDate={selectedWebUserApprovalStartDate}
            selectedWebUserApprovalEndDate={selectedWebUserApprovalEndDate}
            setSelectedWebUserApprovalStartDate={
              setSelectedWebUserApprovalStartDate
            }
            setSelectedWebUserApprovalEndDate={
              setSelectedWebUserApprovalEndDate
            }
            selectedDmsUploadStartDate={selectedDmsUploadStartDate}
            selectedDmsUploadEndDate={selectedDmsUploadEndDate}
            setSelectedDmsUploadStartDate={setSelectedDmsUploadStartDate}
            setSelectedDmsUploadEndDate={setSelectedDmsUploadEndDate}
            selectedStatus={selectedStatus}
            setSelectedStatus={setSelectedStatus}
            selectedPageLimit={selectedPageLimit}
            setSelectedPageLimit={setSelectedPageLimit}
          />
        </div>

        {isLoadingFile && <SpinnerRainbow />}

        {!isLoadingFile && (
          <ManageDataTable
            footer={
              hasData() ? (
                <Pagination
                  onPageChange={onPageChange}
                  data={allFiles && allFiles.meta}
                />
              ) : null
            }
          >
            <table className="file-history">
              <thead>
                <tr>
                  <th className="left">{Translate({ id: "file_id" })} </th>
                  <th className="left">
                    {Translate({ id: "original_filename" })}
                  </th>
                  <th className="left">
                    {Translate({ id: "document_metadata_type" })}
                  </th>
                  <th className="left">{Translate({ id: "deo_name" })} </th>
                  <th className="left">{Translate({ id: "client_name" })} </th>
                  <th className="left">
                    {Translate({ id: "collection_name" })}
                  </th>
                  <th className="left">
                    {Translate({ id: "collection_item_value" })}
                  </th>
                  <th className="left">
                    {Translate({ id: "data_entry_count" })}
                  </th>
                  <th className="left">{Translate({ id: "assigned_to" })}</th>
                  <th className="left">
                    {Translate({ id: "date_of_data_entry" })}
                  </th>
                  <th className="left">
                    {Translate({ id: "date_of_dms_upload" })}
                  </th>
                  <th>{Translate({ id: "batch" })} </th>
                  <th>{Translate({ id: "status" })} </th>
                  <th className="action-col">
                    {Translate({ id: "action" })}
                  </th>
                </tr>
              </thead>

              <tbody>
                {hasData() &&
                  allFiles?.data?.map(item => (
                    <tr key={item.id}>
                      <td>{item.eisenlens_filename}</td>
                      <td>{item.raw_filename}</td>
                      <td>{item.metadata?.title}</td>
                      <td>{item.uploaded_by?.fullname}</td>
                      <td>{item.client?.name}</td>
                      <td>{item.department?.name}</td>
                      <td>{item.department_item_value}</td>
                      <td>{item.characters_entered}</td>
                      <td>
                        {item?.assigned_users
                          ?.map(user => user.fullname)
                          .join(", ")}
                      </td>
                      <td>{getDataEntryDate(item)}</td>
                      <td>{getUploadDate(item)}</td>
                      <td>{item.batch_name}</td>
                      <td>{item.status.toUpperCase()}</td>
                      <td className="action-col">
                        <div className="manage-data-table-actions">
                          <FileDetails fileDoc={item} />
                        </div>
                      </td>
                    </tr>
                  ))}

                {!hasData() && (
                  <tr>
                    <td colSpan="100">
                      <NoData />
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </ManageDataTable>
        )}
      </div>
    </Layout>
  );
};

FileHistory.propTypes = {
  getAllFiles: PropTypes.func,
  allFiles: PropTypes.array,
  allUsers: PropTypes.array,
  isLoadingFile: PropTypes.bool,
  error: PropTypes.object
};

export default FileHistory;
